/**
 * Derive the agent's conversation scope (PHA-549).
 *
 * The scope tells the backend which patient the clinician is actually looking
 * at, and it drives real behaviour: `buildScopeBlocks` injects a "# Current
 * focus" block into the system prompt, and `buildBriefing` pre-fetches that
 * patient's problems, meds, latest BP, care plan and open tasks. Lose the
 * patient scope and the agent still answers — it just answers clinic-wide,
 * without the context, and nothing errors.
 *
 * Extracted from FloatingChat so that invariant is unit-testable. PHA-1071
 * broke it by mounting the chat panel outside PatientTabsProvider, which made
 * `useOptionalPatientTabs()` return null and collapsed every conversation to
 * clinic scope with no type error and no test failure.
 */

import type { AgentChatScope } from '../../services/api';

export interface ScopeTab {
  patientId: string;
  firstName?: string;
  lastName?: string;
}

export interface ScopeTabsContext {
  tabs?: ScopeTab[];
  activeTabId?: string | null;
}

export function deriveChatScope(
  tabsCtx: ScopeTabsContext | null | undefined,
  orgId: string | null | undefined,
  orgName?: string | null
): AgentChatScope | null {
  // Without an organization there is nothing the backend can scope to, and
  // validateScope would reject a mismatched orgId anyway.
  if (!orgId) return null;

  const tabs = tabsCtx?.tabs ?? [];
  const activeTabId = tabsCtx?.activeTabId;
  const activePatientTab = tabs.find((t) => t.patientId === activeTabId);

  if (activePatientTab) {
    const patientName =
      [activePatientTab.firstName, activePatientTab.lastName]
        .filter(Boolean)
        .join(' ') || activePatientTab.patientId;
    return {
      kind: 'patient',
      patientId: activePatientTab.patientId,
      patientName,
      orgId,
    };
  }

  return { kind: 'clinic', orgId, orgName: orgName || '' };
}

/** Stable identity for a scope, used to detect a mid-conversation change. */
export function scopeKeyOf(scope: AgentChatScope | null): string {
  if (!scope) return 'none';
  return `${scope.kind}:${scope.kind === 'patient' ? scope.patientId : scope.orgId}`;
}
